/**
 * RecordingPlayer — full-screen modal that streams a lesson recording through
 * our backend proxy. The `<video>` element uses `controlsList="nodownload"`
 * and disables right-click + picture-in-picture, so the only way to save the
 * file is to inspect network traffic in devtools.
 *
 * The actual MP4 URL is `api.studentRecordingStreamUrl(token, lessonId)` —
 * Daily's signed URL never leaves the server.
 */
import React, { useEffect } from "react";

export default function RecordingPlayer({ src, title, onClose, "data-testid": testId = "recording-player" }) {
  useEffect(() => {
    function onKey(e) { if (e.key === "Escape") onClose?.(); }
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  return (
    <div
      data-testid={testId}
      onClick={onClose}
      className="fixed inset-0 z-[100] bg-black/85 backdrop-blur-sm flex flex-col items-center justify-center p-4 md:p-10"
    >
      <div
        className="w-full max-w-5xl flex items-center justify-between mb-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="text-white">
          <div className="text-[10px] tracking-[0.32em] uppercase text-white/55">Lesson recording</div>
          {title && <div className="font-display text-lg md:text-xl mt-0.5">{title}</div>}
        </div>
        <button
          onClick={onClose}
          data-testid={`${testId}-close`}
          className="px-4 py-2 text-white/80 text-[11px] tracking-[0.28em] uppercase hover:text-white transition-colors"
        >
          Close ✕
        </button>
      </div>
      <div
        className="w-full max-w-5xl rounded-2xl overflow-hidden bg-black"
        onClick={(e) => e.stopPropagation()}
      >
        <video
          data-testid={`${testId}-video`}
          src={src}
          controls
          autoPlay
          controlsList="nodownload noplaybackrate"
          disablePictureInPicture
          onContextMenu={(e) => e.preventDefault()}
          className="w-full h-auto max-h-[80vh] bg-black"
        />
      </div>
      <div className="mt-4 text-[10px] tracking-[0.28em] uppercase text-white/40">
        Available while you're signed in — not downloadable.
      </div>
    </div>
  );
}
